// Debug CPU ray picker: cast a ray from the camera through the loaded map's
// world mesh (and any moving brush entities) and report exactly which draw
// chunk / material it hits.
//
// This exists to pin rendering bugs ("a surface drawn in the wrong place") in
// one shot: given a hit we can say whether the geometry belongs to the static
// world, a portal-surface chunk (reflected FBO), or a mover (brush-entity
// transform), plus the shader name and — via `core.bsp_face_at_triangle` — the
// BSP face index and whether it's a bezier patch. It runs only on demand (a
// debug keypress), so brute force over every triangle is fine.

/** Interleaved vertex stride: pos(3) uv(2) lm(2) normal(3) rgba(4) center(3). */
export const VERTEX_FLOATS = 17;

/** Where a hit's geometry comes from. */
export const PICK_WORLD = 0;
export const PICK_PORTAL = 1;
export const PICK_MOVER = 2;
export type PickKind = 0 | 1 | 2;

/** A contiguous index range to test, tagged with where the geometry comes from. */
export interface PickChunk {
  first: number;
  count: number;
  /** BSP shader index (into `bsp_shader_name`). */
  shader: number;
  kind: PickKind;
  /** Mover model index when `kind === PICK_MOVER`, else -1. */
  mover: number;
}

export interface PickInput {
  verts: Float32Array;
  indices: Uint32Array;
  chunks: PickChunk[];
  moverVerts: Float32Array | null;
  moverIndices: Uint32Array | null;
  moverChunks: PickChunk[];
  /** Per-mover model transform (column-major 4x4), indexed by `chunk.mover`. */
  moverMatrices: Float32Array[];
}

export interface PickHit {
  /** Distance along the (unit) ray. */
  t: number;
  point: [number, number, number];
  /** Authored geometric normal (not flipped to face the camera). */
  normal: [number, number, number];
  /** True if we hit the back side of the triangle. */
  back: boolean;
  /** Triangle index (index offset / 3) within its vertex buffer. */
  triangle: number;
  kind: PickKind;
  /** Index into `chunks` (world/portal) or `moverChunks` (mover). */
  chunk: number;
  first: number;
  count: number;
  shader: number;
  /** Mover model index when `kind === PICK_MOVER`, else -1. */
  mover: number;
}

/**
 * Möller–Trumbore ray/triangle intersection, **double-sided** (a mis-oriented
 * or see-through surface is exactly the bug this tool hunts). Returns the hit
 * distance `t` in `[0, maxT]`, or -1 for a miss.
 */
function rayTri(
  ox: number, oy: number, oz: number,
  dx: number, dy: number, dz: number,
  ax: number, ay: number, az: number,
  bx: number, by: number, bz: number,
  cx: number, cy: number, cz: number,
  maxT: number,
): number {
  const e1x = bx - ax, e1y = by - ay, e1z = bz - az;
  const e2x = cx - ax, e2y = cy - ay, e2z = cz - az;
  const px = dy * e2z - dz * e2y;
  const py = dz * e2x - dx * e2z;
  const pz = dx * e2y - dy * e2x;
  const det = e1x * px + e1y * py + e1z * pz;
  if (det > -1e-9 && det < 1e-9) return -1; // ray parallel to triangle
  const inv = 1 / det;
  const tx = ox - ax, ty = oy - ay, tz = oz - az;
  const u = (tx * px + ty * py + tz * pz) * inv;
  if (u < 0 || u > 1) return -1;
  const qx = ty * e1z - tz * e1y;
  const qy = tz * e1x - tx * e1z;
  const qz = tx * e1y - ty * e1x;
  const v = (dx * qx + dy * qy + dz * qz) * inv;
  if (v < 0 || u + v > 1) return -1;
  const t = (e2x * qx + e2y * qy + e2z * qz) * inv;
  if (t < 0 || t > maxT) return -1;
  return t;
}

function norm3(x: number, y: number, z: number): [number, number, number] {
  const l = Math.hypot(x, y, z) || 1;
  return [x / l, y / l, z / l];
}

/**
 * Cast a ray from `(ox,oy,oz)` along the unit direction `(dx,dy,dz)` and return
 * the nearest front- or back-facing triangle hit, or null.
 */
export function pickRay(
  input: PickInput,
  ox: number, oy: number, oz: number,
  dx: number, dy: number, dz: number,
  maxT = 1e9,
): PickHit | null {
  let best: PickHit | null = null;
  let bestT = maxT;

  // Static world + portal chunks (both index the world buffer).
  {
    const v = input.verts, idx = input.indices;
    for (let ci = 0; ci < input.chunks.length; ci++) {
      const c = input.chunks[ci];
      const end = c.first + c.count;
      for (let k = c.first; k + 3 <= end; k += 3) {
        const a = idx[k] * VERTEX_FLOATS;
        const b = idx[k + 1] * VERTEX_FLOATS;
        const d = idx[k + 2] * VERTEX_FLOATS;
        const t = rayTri(
          ox, oy, oz, dx, dy, dz,
          v[a], v[a + 1], v[a + 2],
          v[b], v[b + 1], v[b + 2],
          v[d], v[d + 1], v[d + 2],
          bestT,
        );
        if (t < 0) continue;
        bestT = t;
        const n = norm3(
          (v[b + 1] - v[a + 1]) * (v[d + 2] - v[a + 2]) - (v[b + 2] - v[a + 2]) * (v[d + 1] - v[a + 1]),
          (v[b + 2] - v[a + 2]) * (v[d] - v[a]) - (v[b] - v[a]) * (v[d + 2] - v[a + 2]),
          (v[b] - v[a]) * (v[d + 1] - v[a + 1]) - (v[b + 1] - v[a + 1]) * (v[d] - v[a]),
        );
        best = {
          t,
          point: [ox + dx * t, oy + dy * t, oz + dz * t],
          normal: n,
          back: n[0] * dx + n[1] * dy + n[2] * dz > 0,
          triangle: k / 3,
          kind: c.kind,
          chunk: ci,
          first: c.first,
          count: c.count,
          shader: c.shader,
          mover: -1,
        };
      }
    }
  }

  // Moving brush entities: transform each triangle by its model matrix.
  if (input.moverVerts && input.moverIndices && input.moverChunks.length) {
    const v = input.moverVerts, idx = input.moverIndices;
    for (let ci = 0; ci < input.moverChunks.length; ci++) {
      const c = input.moverChunks[ci];
      const m = input.moverMatrices[c.mover];
      if (!m) continue;
      const end = c.first + c.count;
      for (let k = c.first; k + 3 <= end; k += 3) {
        const a = idx[k] * VERTEX_FLOATS;
        const b = idx[k + 1] * VERTEX_FLOATS;
        const d = idx[k + 2] * VERTEX_FLOATS;
        const xf = (i: number, o: number) =>
          m[0] * v[i + o] + m[4] * v[i + o + 1] + m[8] * v[i + o + 2] + m[12];
        const yf = (i: number, o: number) =>
          m[1] * v[i + o] + m[5] * v[i + o + 1] + m[9] * v[i + o + 2] + m[13];
        const zf = (i: number, o: number) =>
          m[2] * v[i + o] + m[6] * v[i + o + 1] + m[10] * v[i + o + 2] + m[14];
        const ax = xf(a, 0), ay = yf(a, 0), az = zf(a, 0);
        const bx = xf(b, 0), by = yf(b, 0), bz = zf(b, 0);
        const cx = xf(d, 0), cy = yf(d, 0), cz = zf(d, 0);
        const t = rayTri(ox, oy, oz, dx, dy, dz, ax, ay, az, bx, by, bz, cx, cy, cz, bestT);
        if (t < 0) continue;
        bestT = t;
        const n = norm3(
          (by - ay) * (cz - az) - (bz - az) * (cy - ay),
          (bz - az) * (cx - ax) - (bx - ax) * (cz - az),
          (bx - ax) * (cy - ay) - (by - ay) * (cx - ax),
        );
        best = {
          t,
          point: [ox + dx * t, oy + dy * t, oz + dz * t],
          normal: n,
          back: n[0] * dx + n[1] * dy + n[2] * dz > 0,
          triangle: k / 3,
          kind: PICK_MOVER,
          chunk: ci,
          first: c.first,
          count: c.count,
          shader: c.shader,
          mover: c.mover,
        };
      }
    }
  }

  return best;
}
